import React, { useState, useEffect } from 'react';
import { Text } from '@aws-amplify/ui-react';
import { Box, Backdrop, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import SelectionModal from './SelectionModal';
import './reports.css';

/**
 * BulkDownloadModal - A comprehensive download manager for reports
 *
 * Guides users through selecting download format and options in a step-by-step process
 */
export default function BulkDownloadModal({
  open,
  onClose,
  reports,
  teamColorMap,
  downloadAllChartsAsPDF,
  downloadAllOrgChartsAsPDF,
}) {
  // Step tracking
  const [activeStep, setActiveStep] = useState(0);
  const [downloadType, setDownloadType] = useState('');

  // Selection states
  const [selectedTeams, setSelectedTeams] = useState({});
  const [selectedKpis, setSelectedKpis] = useState({});
  const [searchString, setSearchString] = useState('');
  const [modalType, setModalType] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Extract team and KPI data from reports
  const allTeams = React.useMemo(() => {
    const teams = {};
    if (reports && reports.reports) {
      Object.values(reports.reports).forEach((report) => {
        report.data_sets.forEach((dataset) => {
          teams[dataset.label] = dataset.label;
        });
      });
    }
    return teams;
  }, [reports]);

  const allKpis = React.useMemo(() => {
    const kpis = {};
    if (reports && reports.reports) {
      Object.values(reports.reports).forEach((report) => {
        kpis[report.report_info.id] = report.report_info.title;
      });
    }
    return kpis;
  }, [reports]);

  // Initialize selections when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setDownloadType('');
      setSelectedTeams({ ...allTeams });
      setSelectedKpis({ ...allKpis });
    }
  }, [open, allTeams, allKpis]);

  // Selection modal configuration
  const modalConfiguration = {
    team: {
      type: 'teams',
      fetchFunction: async () => {}, // Not needed as we already have the teams
      optionsList: Object.values(allTeams).map((team) => ({
        id: team,
        name: team,
      })),
      checkedList: selectedTeams,
      checkedListSetter: (id, name) => {
        const temp = { ...selectedTeams };
        if (temp[id]) {
          delete temp[id];
        } else {
          temp[id] = name;
        }
        setSelectedTeams(temp);
      },
      useAvatar: false,
      selectAll: () => setSelectedTeams({ ...allTeams }),
      clearAll: () => setSelectedTeams({}),
      fetchOnStringChange: false,
    },
    kpi: {
      type: 'KPIs',
      fetchFunction: async () => {}, // Not needed as we already have the KPIs
      optionsList: Object.entries(allKpis).map(([id, title]) => ({
        id,
        name: title,
      })),
      checkedList: selectedKpis,
      checkedListSetter: (id, name) => {
        const temp = { ...selectedKpis };
        if (temp[id]) {
          delete temp[id];
        } else {
          temp[id] = name;
        }
        setSelectedKpis(temp);
      },
      useAvatar: false,
      selectAll: () => setSelectedKpis({ ...allKpis }),
      clearAll: () => setSelectedKpis({}),
      fetchOnStringChange: false,
    },
  };

  // Simplify the generateAndDownloadCSV function to only handle standard format
  const generateAndDownloadCSV = (filteredReports, filename) => {
    try {
      // Helper function to format dates consistently
      const formatDate = (dateString, reportData) => {
        // Remove day of week if present (e.g., "Wed 10/30" -> "10/30")
        const dateWithoutDay = dateString.replace(
          /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+/,
          ''
        );

        // Parse the date parts
        const [month, day] = dateWithoutDay
          .split('/')
          .map((part) => parseInt(part.trim(), 10));

        // Determine year based on report data and date patterns
        let year;

        // Check if we have report metadata with dates
        if (reportData && reportData.date_range) {
          // Use the start and end dates from the report
          year = determineYearFromDateRange(month, day, reportData.date_range);
        } else {
          // Fallback to a heuristic approach based on month sequence
          year = inferYearFromSequence(month, dateString, reportData);
        }

        // Format as MM/DD/YYYY
        return `${month}/${day}/${year}`;
      };

      // Helper to determine year from date range
      const determineYearFromDateRange = (month, day, dateRange) => {
        // Extract start and end dates
        let startYear, endYear;

        if (dateRange.start_date) {
          const startDate = new Date(dateRange.start_date);
          startYear = startDate.getFullYear();
        } else {
          startYear = new Date().getFullYear() - 1; // Default to last year
        }

        if (dateRange.end_date) {
          const endDate = new Date(dateRange.end_date);
          endYear = endDate.getFullYear();
        } else {
          endYear = new Date().getFullYear(); // Default to current year
        }

        // If report spans multiple years, determine which year this date falls in
        if (startYear !== endYear) {
          // For dates in later months (Oct-Dec) if the start year includes these months
          if (month >= 10 && startYear) {
            return startYear;
          }
          // For dates in earlier months (Jan-Mar) if the end year includes these months
          else if (month <= 3 && endYear) {
            return endYear;
          }
          // For months in the middle, need more context - default to start year
          else {
            return startYear;
          }
        }

        return startYear || new Date().getFullYear();
      };

      // Helper to infer year based on sequence of dates
      const inferYearFromSequence = (month, dateString, reportData) => {
        // Check if we can infer from the report labels (dates) array
        if (reportData && reportData.labels && reportData.labels.length > 0) {
          // Look at all dates to determine pattern
          const months = reportData.labels
            .map((label) => {
              const datePart = label.replace(
                /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+/,
                ''
              );
              return parseInt(datePart.split('/')[0], 10);
            })
            .filter((m) => !isNaN(m));

          // Check if we have a month crossing December to January
          const hasDecember = months.includes(12);
          const hasJanuary = months.includes(1);

          if (hasDecember && hasJanuary) {
            // We span multiple years
            // If this is January-March and we also have December, this is likely the later year
            if (month <= 3) {
              return new Date().getFullYear(); // Assume current year for early months
            } else if (month >= 10) {
              return new Date().getFullYear() - 1; // Assume previous year for late months
            }
          }
        }

        // Get the user's selected filter period if available
        if (reportData.filter_period) {
          if (reportData.filter_period === 'last_year') {
            return new Date().getFullYear() - 1;
          } else if (reportData.filter_period === 'year_to_date') {
            return new Date().getFullYear();
          }
        }

        // Default to current year if we can't determine
        return new Date().getFullYear();
      };

      // Standard format: Date, Team, KPI, Value
      let csvString = '';
      if (filteredReports.report_type === 'team') {
        csvString = 'Date,Team,KPI,Value\r\n';
      } else {
        csvString = 'Date,KPI,Value\r\n';
      }

      // Process each report that matches selected KPIs
      Object.values(filteredReports.reports).forEach((report) => {
        // Process each dataset (team)
        report.data_sets.forEach((dataset) => {
          const teamName = dataset.label;

          // Add each data point to the CSV
          dataset.data.forEach((value, index) => {
            if (value !== null && value !== undefined) {
              const date = formatDate(report.labels[index], report);
              if (filteredReports.report_type === 'team') {
                csvString += `"${date}","${teamName}","${report.report_info.title}",${value}\r\n`;
              } else {
                csvString += `"${date}","${report.report_info.title}",${value}\r\n`;
              }
            }
          });
        });
      });

      // Create and download the CSV file
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename || 'report_export.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true;
    } catch (error) {
      console.error('Error generating CSV:', error);
      return false;
    }
  };

  // Navigate to the next step
  const handleNext = () => {
    // If on first step, go to second step
    if (activeStep === 0) {
      setActiveStep(1);
      return;
    }

    // Otherwise, start processing the download
    setIsProcessing(true);

    // Handle CSV export
    if (downloadType === 'csv') {
      try {
        // Generate filename
        const filename = `${reports.org_id}_kpi_report_${
          new Date().toISOString().split('T')[0]
        }.csv`;

        // Generate and download the CSV directly - using standard format
        const success = generateAndDownloadCSV(reports, filename);

        // Update UI state regardless of success
        setTimeout(() => {
          setIsProcessing(false);
          if (success) {
            onClose();
          }
        }, 500);
      } catch (error) {
        console.error('Error in CSV export:', error);
        setIsProcessing(false);
      }
    }
    // Handle PDF export - simplified for both team and org reports
    else if (downloadType === 'pdf') {
      try {
        // Pass the entire reports object without customization
        if (reports.report_type === 'team') {
          downloadAllChartsAsPDF(reports, teamColorMap);
        } else if (reports.report_type === 'org') {
          downloadAllOrgChartsAsPDF(reports);
        } else {
          // This shouldn't happen with current design
          console.log('Unsupported report type for PDF export');
        }

        // PDF generation is handled asynchronously
        onClose();
      } catch (error) {
        console.error('Error in PDF export:', error);
        setIsProcessing(false);
      }
    }
  };

  // Go back to the previous step
  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(0, prevStep - 1));
  };

  // Render the content for the current step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Text
              style={{
                fontWeight: 'bold',
                fontSize: '18px',
                marginBottom: '10px',
                textAlign: 'center',
              }}
            >
              Select download format:
            </Text>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 4,
                width: '100%',
                maxWidth: '500px',
              }}
            >
              <Box
                className={`format-option ${
                  downloadType === 'csv' ? 'selected' : ''
                }`}
                onClick={() => {
                  setDownloadType('csv');
                  setActiveStep(1);
                }}
                sx={{
                  flex: '1',
                  maxWidth: '200px',
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor:
                    downloadType === 'csv'
                      ? 'var(--hover-color)'
                      : 'var(--background-color-2)',
                  borderColor:
                    downloadType === 'csv'
                      ? 'var(--primary-color)'
                      : 'var(--border-color)',
                  '&:hover': {
                    borderColor: 'var(--primary-color)',
                    backgroundColor: 'var(--hover-color)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <TableChartIcon
                  style={{
                    fontSize: '60px',
                    color:
                      downloadType === 'csv'
                        ? 'var(--primary-color)'
                        : 'var(--text-color-light)',
                    marginBottom: '15px',
                  }}
                />
                <Text
                  style={{
                    fontSize: '18px',
                    fontWeight: downloadType === 'csv' ? 'bold' : 'normal',
                    color:
                      downloadType === 'csv'
                        ? 'var(--primary-color)'
                        : 'var(--text-color)',
                  }}
                >
                  CSV File
                </Text>
              </Box>

              <Box
                className={`format-option ${
                  downloadType === 'pdf' ? 'selected' : ''
                }`}
                onClick={() => {
                  setDownloadType('pdf');
                  setActiveStep(1);
                }}
                sx={{
                  flex: '1',
                  maxWidth: '200px',
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor:
                    downloadType === 'pdf'
                      ? 'var(--hover-color)'
                      : 'var(--background-color-2)',
                  borderColor:
                    downloadType === 'pdf'
                      ? 'var(--primary-color)'
                      : 'var(--border-color)',
                  '&:hover': {
                    borderColor: 'var(--primary-color)',
                    backgroundColor: 'var(--hover-color)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <PictureAsPdfIcon
                  style={{
                    fontSize: '60px',
                    color:
                      downloadType === 'pdf'
                        ? 'var(--primary-color)'
                        : 'var(--text-color-light)',
                    marginBottom: '15px',
                  }}
                />
                <Text
                  style={{
                    fontSize: '18px',
                    fontWeight: downloadType === 'pdf' ? 'bold' : 'normal',
                    color:
                      downloadType === 'pdf'
                        ? 'var(--primary-color)'
                        : 'var(--text-color)',
                  }}
                >
                  PDF File
                </Text>
              </Box>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Text style={{ fontWeight: 'bold', fontSize: '18px' }}>
              {downloadType === 'csv'
                ? 'CSV Export Options'
                : 'PDF Export Options'}
            </Text>

            {downloadType === 'csv' && (
              <Box
                sx={{
                  padding: '10px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--background-color)',
                }}
              >
                <Text>Standard format (Date, Team, KPI, Value)</Text>
                <Text
                  sx={{
                    fontSize: '12px',
                    color: 'var(--text-color-light)',
                    marginTop: '4px',
                  }}
                >
                  Exports data in a single table with one row per data point
                </Text>
              </Box>
            )}

            {downloadType === 'pdf' && (
              <Box>
                <Text sx={{ fontSize: '16px', mb: 2 }}>
                  The PDF will include all charts currently displayed in the
                  report.
                </Text>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '4px',
                  }}
                >
                  <Text sx={{ fontWeight: 'bold', mb: 1 }}>Report Summary</Text>
                  <Text>
                    • Report Type: {}
                    {reports.report_type === 'team'
                      ? 'Team KPI'
                      : 'Organization KPI'}
                  </Text>
                  <Text>
                    • Charts: {}
                    {Array.isArray(reports.reports)
                      ? reports.reports.length
                      : Object.keys(reports.reports).length}
                  </Text>
                  <Text>• Organization ID: {reports.org_id}</Text>
                </Box>

                <Text
                  sx={{
                    mt: 2,
                    fontSize: '14px',
                    color: 'var(--text-color-light)',
                    fontStyle: 'italic',
                  }}
                >
                  Click &quot;Download&quot; to generate and save the PDF file.
                </Text>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={open}
      >
        <Box
          sx={{
            position: 'relative',
            width: '90%',
            maxWidth: '800px',
            backgroundColor: 'var(--background-color-2)',
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              borderBottom: '1px solid var(--border-color)',
            }}
          >
            <Text
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
              }}
            >
              Download Reports
            </Text>
            <Box
              onClick={onClose}
              sx={{
                cursor: 'pointer',
                fontSize: '24px',
                color: 'var(--text-color-light)',
                '&:hover': {
                  color: 'var(--primary-color)',
                },
              }}
            >
              ×
            </Box>
          </Box>

          <Box sx={{ height: '400px', overflow: 'auto' }}>
            {getStepContent(activeStep)}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <Box
              onClick={activeStep === 0 ? onClose : handleBack}
              sx={{
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Box>

            <Box
              onClick={handleNext}
              sx={{
                padding: '8px 24px',
                backgroundColor:
                  activeStep === 0 && !downloadType
                    ? 'var(--border-color)'
                    : 'var(--primary-color)',
                color:
                  activeStep === 0 && !downloadType
                    ? 'var(--text-color-light)'
                    : 'var(--text-cta)',
                borderRadius: '4px',
                cursor:
                  activeStep === 0 && !downloadType ? 'default' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {isProcessing ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <>
                  {activeStep === 0 ? 'Next' : 'Download'}
                  {activeStep === 1 && !isProcessing && (
                    <DownloadIcon sx={{ ml: 1 }} />
                  )}
                </>
              )}
            </Box>
          </Box>

          {modalType && (
            <SelectionModal
              config={modalConfiguration[modalType]}
              searchString={searchString}
              setSearchString={setSearchString}
              open={Boolean(modalType)}
              close={() => {
                setModalType(null);
                setSearchString('');
              }}
            />
          )}
        </Box>
      </Backdrop>
    </div>
  );
}
